import apiClient from "./apiClient";

/**
 * Client for the admin watermark-recovery endpoint (ADR-087, "Recovery API").
 *
 * POST /admin/grants/watermark/recover — attributes a leaked fragment of nabór
 * data (pasted from a competitor site) back to the `userId` whose invisible
 * per-user fingerprint it carries. Backend guard: `GrantsAdminGuard`
 * (role === "admin"). Rate limited to 10/min. The suspect text is sent as-is —
 * the caller MUST paste the copied text verbatim so the zero-width fingerprint
 * survives; retyping strips it.
 */

/** Max length accepted by the backend DTO (`RecoverWatermarkDto`). */
export const MAX_SUSPECT_TEXT_LENGTH = 20000;

export interface RecoverWatermarkPayload {
  /** Verbatim text copied from the competitor — carries the hidden token. */
  suspectText: string;
  /** Optional ISO date (YYYY-MM-DD) — narrows/widens the nonce search window. */
  since?: string;
  /** Optional ISO date (YYYY-MM-DD). */
  until?: string;
}

export type RecoveryUnresolvedReason =
  | "NO_MATCH"
  | "AMBIGUOUS"
  | "NORMALIZED_BEYOND_RECOVERY";

export interface RecoveryCandidate {
  userId: string;
  email: string;
  /** UTC day (YYYY-MM-DD) of the matched nonce bucket for this candidate. */
  copyDate: string;
  /** Raw matched nonce bucket (days since Unix epoch, UTC). */
  copyNonce: number;
}

/**
 * Mirrors the backend `RecoveryResult` union (RecoverySuccess |
 * RecoveryUnresolved). `resolved: true` carries `userId`/`email` plus
 * `copyDate`/`copyNonce` (the UTC day the mark was served to / fetched by that
 * account — a cryptographically-bound timestamp, ADR-087 §4); otherwise
 * `reason` explains why, and `candidates` is present only for `AMBIGUOUS`.
 * `accessTimestamps` exists only on the success shape (empty today — L1 access
 * logs live in Loki, correlated by hand).
 */
export interface RecoverWatermarkResponse {
  resolved: boolean;
  userId?: string;
  email?: string;
  /** UTC day (YYYY-MM-DD) the mark was served/fetched — present on success. */
  copyDate?: string;
  /** Raw matched nonce bucket — present on success. */
  copyNonce?: number;
  reason?: RecoveryUnresolvedReason;
  candidates?: RecoveryCandidate[];
  accessTimestamps?: string[];
}

export const recoverWatermark = async (
  payload: RecoverWatermarkPayload
): Promise<RecoverWatermarkResponse> => {
  const { data } = await apiClient.post<RecoverWatermarkResponse>(
    "/admin/grants/watermark/recover",
    payload
  );
  return data;
};
